import { useCartActions, useMarket } from "@app/_context";
import type { CustomProduct } from "@apptus/esales-api";
import { createProductRules } from "@server/requests/elevate/createProductRules";
import getRawLandingPage from "@server/requests/elevate/getRawLandingPage";
import { elevateProductToNgVariant } from "@server/requests/elevate/mapElevateVariants";
import { useMutation } from "@tanstack/react-query";
import type { Variant } from "types";

export type StringPlannerData = {
  designid: string;
  name: string;
  items: {
    ean: string;
    article: string;
    quantity: number;
  }[];
};

export default function useStringPlanner() {
  const { market } = useMarket().state;
  const { addCartItem } = useCartActions();

  return useMutation({
    mutationKey: ["string-planner"],
    mutationFn: async (data: StringPlannerData) => {
      const values = data.items.map((item) => item.ean);
      const [rules] = createProductRules({
        attribute: "custom.ean",
        values,
      });
      const dynamicPage = await getRawLandingPage(
        market,
        {
          notify: false,
          pageReference: "dynamic-page",
          presentCustom: ["ean"],
        },
        {
          primaryList: {
            include: true,
            ...rules,
          },
        },
      );

      if (!dynamicPage) {
        return;
      }

      const products = dynamicPage.primaryList?.productGroups.flatMap(
        (pg) => pg.products as CustomProduct[],
      );

      const items = data.items
        .map(({ ean, quantity }) => {
          const elevateProduct = products?.find(
            (product) => product.custom?.ean?.[0]?.id === ean,
          );

          if (!elevateProduct) {
            return;
          }

          const item = elevateProductToNgVariant({
            product: elevateProduct as CustomProduct,
            marketConfig: market,
          });

          return { item, quantity };
        })
        .filter((item): item is { item: Variant; quantity: number } =>
          Boolean(item),
        );

      if (items.length) {
        void addCartItem(items);
      }
    },
  });
}
